import { useCallback, useMemo, useRef, useState } from 'react';

import type { TreeViewDefaultItemModelProperties } from '@mui/x-tree-view';
import { useLocalStorage } from 'react-use';
import { Apollo } from 'src/gql/generated';
import type { LabelSubset } from 'src/types';
import { z } from 'zod';

import { selectionStateVar, setSelectionState } from './FileStorageSyncDialog';

export interface FolderSyncJobSubset extends Pick<
  Apollo.FolderSyncJob,
  | 'allowedMimeTypes'
  | 'completedIds'
  | 'driveId'
  | 'driveName'
  | 'failedFiles'
  | 'folderName'
  | 'id'
  | 'syncFinishedAt'
  | 'syncStartedAt'
  | 'folderId'
  | 'storageIntegrationIds'
  | '__typename'
> {
  isFullSync: boolean;
  labels: LabelSubset[];
}

export type GroupedJobs = Record<
  string,
  { driveName: string; jobs: FolderSyncJobSubset[]; totalFiles: number }
>;

export enum ResourceType {
  Drive = 'drive',
  File = 'file',
  Folder = 'folder',
}

export interface TreeNode extends TreeViewDefaultItemModelProperties {
  id: string;
  label: string;
  type: ResourceType;
  children?: TreeNode[];
  childrenCount?: number;
  mimeType?: string;
  parentId?: string;
}

type ItemType = TreeNode;

export type FolderSelection = { id: string; name: string };

export type DriveSelection = {
  folderIds: Set<FolderSelection>;
  jobType: Apollo.JobType;
};

export type UseFolderSyncJobsReturn = ReturnType<typeof useFolderSyncJobs>;

const FolderSyncStructureSchema = z.array(
  z.object({
    driveId: z.string(),
    driveName: z.string(),
    jobs: z.array(z.object({ id: z.string(), title: z.string() })),
  })
);

export type FolderSyncStructureDrive = z.infer<typeof FolderSyncStructureSchema>[number];

export function useFolderSyncStructure(integration: Apollo.FileStorageIntegration) {
  const [structure = [], setStructure] = useLocalStorage<FolderSyncStructureDrive[]>(
    `integrations.folderSyncStructure.${integration}`,
    [],
    {
      deserializer: raw => FolderSyncStructureSchema.catch([]).parse(JSON.parse(raw)),
      raw: false,
      serializer: JSON.stringify,
    }
  );
  return { setStructure, structure };
}

export function getFolderSyncJobTitle(job: Pick<FolderSyncJobSubset, 'folderName' | 'isFullSync'>) {
  return job.isFullSync ? 'All files and folders' : job.folderName || 'Untitled';
}

export function useFolderSyncJobs(integration: Apollo.FileStorageIntegration) {
  const { data, error, loading, previousData } = Apollo.useGetIntegrationFolderSyncJobsQuery({
    fetchPolicy: 'cache-and-network',
    variables: { integration },
  });
  const jobsData = data ?? previousData;
  const { grouped, hasValues, totalJobs } = useMemo(() => {
    const jobs = jobsData?.folderSyncJobsForConnection ?? [];

    if (!jobs.length) return { grouped: {}, hasValues: false, totalJobs: 0 };

    const grouped = groupJobsByDrive(jobs);

    return { grouped, hasValues: Boolean(Object.keys(grouped).length), totalJobs: jobs.length };
  }, [jobsData?.folderSyncJobsForConnection]);

  return {
    data: jobsData?.folderSyncJobsForConnection,
    error,
    grouped,
    hasValues,
    initialLoading: loading && !jobsData,
    isEmpty: !hasValues && !loading,
    loading,
    totalJobs,
  };
}

/**
 * Groups folder sync jobs by drive ID
 */
export function groupJobsByDrive(
  jobs: Apollo.GetIntegrationFolderSyncJobsQuery['folderSyncJobsForConnection']
): GroupedJobs {
  return jobs.reduce<GroupedJobs>((acc, job) => {
    const driveId = job.driveId;

    if (!acc[driveId]) {
      acc[driveId] = {
        driveName: job.driveName,
        jobs: [],
        totalFiles: 0,
      };
    }

    acc[driveId].jobs.push({
      ...job,
      isFullSync: job.folderId === 'root' || job.driveId === job.folderId,
    });
    acc[driveId].totalFiles += getFolderSyncJobFileIds(job).length;

    return acc;
  }, {});
}

export function getFolderSyncJobFileIds(
  job: Pick<
    Apollo.FolderSyncJob,
    'completedIds' | 'failedFiles' | 'storageIntegrationIds' | '__typename'
  >
) {
  const dismissedFailedFileIds = new Set(
    job.failedFiles.filter(f => f.isDimissed).map(f => f.fileId)
  );
  const allFailedFileIds = job.failedFiles.map(f => f.fileId);
  return [
    ...new Set([...job.completedIds, ...job.storageIntegrationIds, ...allFailedFileIds]),
  ].filter(id => !dismissedFailedFileIds.has(id));
}

type FolderChildrenPageMeta = {
  hasMore: boolean;
  nextCursor: string | null;
};

type FolderChildrenPaginationCache = {
  driveId: string;
  fetchType: 'folder' | 'drive';
  hasMore: boolean;
  items: ItemType[];
  nextCursor: string | null;
};

export type FolderChildrenPaginationState = {
  dataLength: number;
  hasMore: boolean;
  lastItemId: string | null;
};

type FolderFileLike = {
  id: string;
  name: string;
  mimeType?: string | null;
  type?: Apollo.FileType | null;
};

export function mapConnectionFilesToTreeNodes(
  files: FolderFileLike[],
  parentId: string
): ItemType[] {
  return files
    .map<ItemType>(file => ({
      children: [],
      // Returning 1 ensures that the Drive/Folder is expandable in the UI...
      childrenCount: file.type !== Apollo.FileType.File ? 1 : 0,
      id: file.id,
      label: file.name,
      mimeType: file.mimeType ?? undefined,
      parentId,
      type: file.type === Apollo.FileType.Folder ? ResourceType.Folder : ResourceType.File,
    }))
    .sort(sortItemTypesComparator);
}

function toFolderFileLikes(
  files: Array<
    | {
        id?: string | null;
        mimeType?: string | null;
        name?: string | null;
        type?: Apollo.FileType | null;
      }
    | null
    | undefined
  >
): FolderFileLike[] {
  return files.flatMap(file => {
    if (!file?.id || !file.name) return [];
    return [
      {
        id: file.id,
        mimeType: file.mimeType,
        name: file.name,
        type: file.type,
      },
    ];
  });
}

export function appendFolderChildrenPage(
  existingItems: ItemType[],
  pageItems: ItemType[]
): ItemType[] {
  return [...existingItems, ...pageItems].sort(sortItemTypesComparator);
}

export function useFetchFolderChildren(integration: Apollo.FileStorageIntegration) {
  const [getFolderFiles] = Apollo.useGetConnectionFolderFilesLazyQuery({
    fetchPolicy: 'network-only',
  });
  const loadingMoreParentIdsRef = useRef(new Set<string>());
  const paginationByParentRef = useRef(new Map<string, FolderChildrenPaginationCache>());
  const [loadingMoreParentIds, setLoadingMoreParentIds] = useState(new Set<string>());
  const [paginationByParent, setPaginationByParent] = useState(
    new Map<string, FolderChildrenPaginationState>()
  );

  const publishPaginationState = useCallback(
    (parentId: string, state: FolderChildrenPaginationCache) => {
      setPaginationByParent(current => {
        const updated = new Map(current);
        updated.set(parentId, {
          dataLength: state.items.length,
          hasMore: state.hasMore,
          lastItemId: state.items.at(-1)?.id ?? null,
        });
        return updated;
      });
    },
    []
  );

  const syncTreeMapWithNodes = useCallback((parentId: string, nodes: ItemType[]) => {
    const currentState = selectionStateVar();
    const updatedTreeMap = new Map(currentState.treeMap);
    const parentNode = currentState.treeMap.get(parentId);
    if (parentNode) {
      updatedTreeMap.set(parentId, {
        ...parentNode,
        childrenCount: nodes.length,
      });
    }

    nodes.forEach(node => updatedTreeMap.set(node.id, node));
    setSelectionState({ treeMap: updatedTreeMap });
  }, []);

  const fetchFolderPage = useCallback(
    async ({
      cursor,
      driveId,
      fetchType,
      parentId,
    }: {
      driveId: string;
      fetchType: 'folder' | 'drive';
      parentId: string;
      cursor?: string | null;
    }): Promise<{ meta: FolderChildrenPageMeta; nodes: ItemType[] }> => {
      const { data } = await getFolderFiles({
        variables: {
          cursor: cursor ?? undefined,
          driveId,
          folderId: fetchType === 'folder' ? parentId : undefined,
          integration,
        },
      });
      const page = data?.connectionFolderFiles;
      const nodes = mapConnectionFilesToTreeNodes(toFolderFileLikes(page?.files ?? []), parentId);
      const meta = page?.meta;

      return {
        meta: {
          hasMore: Boolean(meta?.hasMore),
          nextCursor: meta?.nextCursor ?? null,
        },
        nodes,
      };
    },
    [getFolderFiles, integration]
  );

  const fetchData = useCallback(
    async (parentId: string, fetchType: 'folder' | 'drive') => {
      const existing = paginationByParentRef.current.get(parentId);
      if (existing) {
        return existing.items;
      }

      const currentState = selectionStateVar();
      const driveId =
        fetchType === 'drive' ? parentId : getDriveIdFromNodeId(parentId, currentState.treeMap);
      const { meta, nodes } = await fetchFolderPage({
        driveId,
        fetchType,
        parentId,
      });

      paginationByParentRef.current.set(parentId, {
        driveId,
        fetchType,
        hasMore: meta.hasMore,
        items: nodes,
        nextCursor: meta.nextCursor,
      });
      syncTreeMapWithNodes(parentId, nodes);
      publishPaginationState(parentId, {
        driveId,
        fetchType,
        hasMore: meta.hasMore,
        items: nodes,
        nextCursor: meta.nextCursor,
      });

      return nodes;
    },
    [fetchFolderPage, publishPaginationState, syncTreeMapWithNodes]
  );

  const fetchMoreChildren = useCallback(
    async (parentId: string) => {
      const existing = paginationByParentRef.current.get(parentId);
      if (!existing?.hasMore || !existing.nextCursor) return null;
      if (loadingMoreParentIdsRef.current.has(parentId)) return null;

      try {
        loadingMoreParentIdsRef.current.add(parentId);
        setLoadingMoreParentIds(current => new Set(current).add(parentId));
        const { meta, nodes: pageNodes } = await fetchFolderPage({
          cursor: existing.nextCursor,
          driveId: existing.driveId,
          fetchType: existing.fetchType,
          parentId,
        });
        const items = appendFolderChildrenPage(existing.items, pageNodes);
        const nextState: FolderChildrenPaginationCache = {
          ...existing,
          hasMore: meta.hasMore,
          items,
          nextCursor: meta.nextCursor,
        };
        paginationByParentRef.current.set(parentId, nextState);
        syncTreeMapWithNodes(parentId, items);
        publishPaginationState(parentId, nextState);

        return parentId;
      } finally {
        loadingMoreParentIdsRef.current.delete(parentId);
        setLoadingMoreParentIds(current => {
          const updated = new Set(current);
          updated.delete(parentId);
          return updated;
        });
      }
    },
    [fetchFolderPage, publishPaginationState, syncTreeMapWithNodes]
  );

  return {
    fetchData,
    fetchMoreChildren,
    loadingMoreParentIds,
    paginationByParent,
  };
}

export function sortItemTypesComparator(a: ItemType, b: ItemType): number {
  if (a.type !== b.type) {
    if (a.type === ResourceType.File && b.type !== ResourceType.File) return 1;
    if (b.type === ResourceType.File && a.type !== ResourceType.File) return -1;
  }

  return a.label.localeCompare(b.label, undefined, { numeric: true });
}

export function useInitialTreeView(integration: Apollo.FileStorageIntegration) {
  const { data: drivesData, loading: drivesLoading } = Apollo.useGetDrivesQuery({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: { integration },
  });
  const rootItems = useMemo<ItemType[]>(() => {
    const currentState = selectionStateVar();
    const newItems =
      drivesData?.drives.map<ItemType>(drive => {
        const type = getTreeNodeType(drive);

        return {
          children: [],
          childrenCount: type !== ResourceType.File ? 1 : 0,
          driveId: drive.id,
          id: drive.id,
          label: drive.name,
          type,
        };
      }) ?? [];
    newItems.forEach(node => currentState.treeMap.set(node.id, node));

    return [...newItems].sort(sortItemTypesComparator);
  }, [drivesData?.drives]);

  return {
    drivesLoading,
    rootItems,
  };
}

type DriveLikeSubset =
  | Pick<Apollo.Drive, '__typename'>
  | Pick<Apollo.FolderFile, '__typename' | 'type'>;

function getTreeNodeType(drive: DriveLikeSubset): ResourceType {
  switch (drive.__typename) {
    case 'Drive':
      return ResourceType.Drive;
    case 'FolderFile':
      return drive.type === Apollo.FileType.Folder ? ResourceType.Folder : ResourceType.File;
  }
}

function getDriveName(nodeId: string, treeMap: Map<string, TreeNode>): string {
  const node = treeMap.get(nodeId);
  if (!node) return '';

  // If this node has no parent, it's the root drive.
  if (!node.parentId) return node.label;

  // Recursively find the topmost drive by traversing up the parent chain.
  return getDriveName(node.parentId, treeMap);
}

export function toDriveSyncs(args: {
  allowedMimeTypes: string[];
  folderSyncFolderIds: string[];
  selectedIds: Set<string>;
  treeMap: Map<string, TreeNode>;
}): Apollo.BulkCreateFolderSyncJobInput['driveSyncs'] {
  const { allowedMimeTypes, folderSyncFolderIds, selectedIds, treeMap } = args;
  const rootMostNodes = getRootMostNodes(selectedIds, treeMap);
  const driveIdToSelectedFolders = [...rootMostNodes].reduce<Record<string, Apollo.DriveSyncInput>>(
    (acc, item) => {
      const driveId = getDriveIdFromNodeId(item.id, treeMap);

      if (!acc[driveId])
        acc[driveId] = {
          allowedMimeTypes,
          folderIds: [],
          id: driveId,
          jobType: Apollo.JobType.Sync,
          name: getDriveName(item.id, treeMap),
        };
      if (item.type !== ResourceType.File && !folderSyncFolderIds.includes(item.id))
        acc[driveId].folderIds.push({ id: item.id, name: item.label });

      return acc;
    },
    {}
  );
  const syncJobs = Object.values(driveIdToSelectedFolders);
  const unsyncJobs = [...rootMostNodes].reduce<Apollo.DriveSyncInput[]>((acc, item) => {
    if (item.type === ResourceType.File) return acc;

    const isAncestorToExistingFolderSyncFolderId = folderSyncFolderIds.some(existingFolderId =>
      isDescendantOf({ ancestorId: item.id, descendantId: existingFolderId, treeMap })
    );

    /**
     * If the selected folder is an ancestor to any existing FolderSyncJob, we must delete/unsync that job
     * at the Server b/c we only want "root" FolderSyncJobs.
     */
    if (isAncestorToExistingFolderSyncFolderId) {
      const driveId = getDriveIdFromNodeId(item.id, treeMap);
      const descendantFolders = getDescendantFolders(item.id, treeMap, folderSyncFolderIds);

      if (descendantFolders.length) {
        acc.push({
          allowedMimeTypes,
          folderIds: descendantFolders,
          id: driveId,
          jobType: Apollo.JobType.Unsync,
          name: getDriveName(driveId, treeMap),
        });
      }
    }

    return acc;
  }, []);

  return [...syncJobs, ...unsyncJobs];
}

export function getDriveIdFromNodeId(nodeId: string, treeMap: Map<string, TreeNode>): string {
  const node = treeMap.get(nodeId);
  if (!node) throw new Error(`Node with ID ${nodeId} not found in treeMap.`);

  if (!node.parentId) return nodeId;

  return getDriveIdFromNodeId(node.parentId, treeMap);
}

/**
 * Checks if descendantId node is a descendant of ancestorId node.
 */
function isDescendantOf(args: {
  ancestorId: string;
  descendantId: string;
  treeMap: Map<string, TreeNode>;
}): boolean {
  const { ancestorId, descendantId, treeMap } = args;
  const state = {
    currentId: descendantId,
  };

  while (state.currentId) {
    const node = treeMap.get(state.currentId);

    if (!node?.parentId) break;
    if (node.parentId === ancestorId) return true;

    state.currentId = node.parentId;
  }

  return false;
}

/**
 * Gets all descendant folders of a given folder that are *in the folderSyncFolderIds* list.
 * This gives us an array of folderIds that we need to unsync at the Server.
 */
function getDescendantFolders(
  folderId: string,
  treeMap: Map<string, TreeNode>,
  folderSyncFolderIds: string[]
): Array<{ id: string; name: string }> {
  const descendants: Array<{ id: string; name: string }> = [];

  // Find all nodes in folderSyncFolderIds that are descendants of this folder.
  for (const existingFolderId of folderSyncFolderIds) {
    if (isDescendantOf({ ancestorId: folderId, descendantId: existingFolderId, treeMap })) {
      const node = treeMap.get(existingFolderId);
      if (node && node.type !== ResourceType.File)
        descendants.push({ id: existingFolderId, name: node.label });
    }
  }

  return descendants;
}

/**
 * Gets the root-most tree nodes from a set of selected IDs in a tree structure.
 * A node is considered root-most if it has no parent or if none of its ancestors are in the selectedIds set.
 */
export function getRootMostNodes(
  selectedIds: Set<string>,
  treeMap: Map<string, TreeNode>
): Set<TreeNode> {
  const rootMostNodes = new Set<TreeNode>();

  for (const id of selectedIds) {
    const node = treeMap.get(id);
    if (!node) continue;

    // If the node has no parent, it's root-most.
    if (!node.parentId) {
      rootMostNodes.add(node);
      continue;
    }

    // Check if any ancestor (parent, grandparent, etc.) is in the selectedIds set.
    const state = {
      currentParentId: node.parentId,
      hasSelectedAncestor: false,
    };

    while (state.currentParentId) {
      if (selectedIds.has(state.currentParentId)) {
        state.hasSelectedAncestor = true;
        break;
      }

      const parentNode = treeMap.get(state.currentParentId);
      if (!parentNode?.parentId) break;

      state.currentParentId = parentNode.parentId;
    }

    // If no ancestor is selected, this node is root-most
    if (!state.hasSelectedAncestor) rootMostNodes.add(node);
  }

  return rootMostNodes;
}

export function useSetFolderSyncJobLabels() {
  const [setFolderSyncJobLabels, { loading }] = Apollo.useSetFolderSyncJobLabelsMutation();

  const safeSetFolderSyncJobLabels = useCallback(
    async ({ folderSyncJobId, labels }: { folderSyncJobId: string; labels: LabelSubset[] }) => {
      return setFolderSyncJobLabels({
        optimisticResponse: {
          __typename: 'Mutation',
          setFolderSyncJobLabels: {
            __typename: 'FolderSyncJob',
            id: folderSyncJobId,
            labels,
          },
        },
        variables: { folderSyncJobId, labelIds: labels.map(l => l.id) },
      });
    },
    [setFolderSyncJobLabels]
  );

  return {
    loading,
    setFolderSyncJobLabels: safeSetFolderSyncJobLabels,
  };
}
